import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'react-string-format'
import moment from 'moment'
import { Template } from 'devextreme-react/core/template'
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Paging,
  Pager,
  Scrolling,
  Export
} from 'devextreme-react/data-grid'
import { Tooltip, Icon, Menu, MenuItem, Popover, Position, PopoverInteractionKind } from '@blueprintjs/core'
/* API LIST */
import { getDeliveryDetailHeaderRows } from '../../api/delivery'
import { getDeliveryListShortcuts } from '../../api/shortcuts'
/* CUSTOM HOOKS */
import useAlert from '../../lib/useAlert'
import { userHasMenu } from 'lib/userHasMenu'
/* COMPONENTS */
import Stimulsoft from '../../components/Stimulsoft'
import ViewCell from '../../components/DevExpressGrid/ViewCell'
import PrintCell from '../../components/DevExpressGrid/PrintCell'

import ToolbarRefreshButton from '../../components/DevExpressGrid/ToolbarRefreshButton'
import ToolbarExportButton from '../../components/DevExpressGrid/ToolbarExportButton'
import GridButton from 'components/DevExpressGrid/GridButton'
import DeliveryBomiStatus from 'views/DeliveryBomiStatus'
/* PAGES */
import DeliveryDetail from '../DeliveryDetail'
import OrderShortcuts from '../Shortcuts/Orders'
import ContractShortcuts from '../Shortcuts/Contracts'
import InvoiceShortcuts from '../Shortcuts/Invoices'
import BPDialogV2 from '../../components/BPDialogV2'

const DeliveryShortcuts = (props) => {
  const { t, i18n } = useTranslation()
  const { openAlert } = useAlert()
  const [deliveries, setDeliveries] = useState([])
  const [printData, setPrintData] = useState([])
  const [documentNo, setDocumentNo] = useState({})
  const [SAPDeliveryNo, setSAPDeliveryNo] = useState()

  const refGrid = useRef()

  /* DIALOG REFS */
  const refDeliveryDetailDialog = useRef()
  const refPrintDialog = useRef()
  const refOrderShortcutsDialog = useRef()
  const refInvoiceShortcutsDialog = useRef()
  const refContractShortcutsDialog = useRef()
  const refBomiStatusDialog = useRef()
  /* DIALOG REFS */

  const generateExportFileName = format(t('export_filename_delivery_shortcuts'), moment().format('DD.MM.YYYY'))

  const getDeliveries = async () => {
    try {
      const params = {
        start_date: null,
        end_date: null,
        vbeln: props.contract_number ? props.contract_number : null,
        sap_order_number: props.sap_order_number ? props.sap_order_number : null,
        sap_invoice_number: props.sap_invoice_number ? props.sap_invoice_number : null,
        material: props.material || null
      }
      const { data, status } = await getDeliveryListShortcuts(params)
      if (data && status === 200 && data.P_RESULT_CODE === 0) {
        const deliveryList = JSON.parse(JSON.stringify(JSON.parse(data.RESULT)).replace(/"([^"]+)":/g, function($0, $1) { return ('"' + $1.toLowerCase() + '":'); }));
        setDeliveries(deliveryList)
        //setDeliveries(data.RESULT)
      } else {
        openAlert('error', true, data.P_RESULT_MESSAGE)
      }
    } catch (error) {
      openAlert('error', true, error.message)
    }
  }

  const cbGetDeliveries = useCallback(getDeliveries, [])

  useEffect(() => {
    cbGetDeliveries()
  }, [cbGetDeliveries])

  const getDeliveryDetail = async (id) => {
    try {
      const { data, status } = await getDeliveryDetailHeaderRows(id)
      if (data && status === 200 && data.P_RESULT_CODE === 0) {
        setPrintData(data.RESULT[0].data)
      } else {
        openAlert('error', true, data.P_RESULT_MESSAGE)
      }
    } catch (error) {
      openAlert('error', true, error.message)
    }
  }

  const onViewCellClick = (data) => {
    setDocumentNo(data.pi_message)
    refDeliveryDetailDialog.current.show()
  }
  const onPrintCellClick = (data) => {
    getDeliveryDetail(data.pi_message)
    refPrintDialog.current.show()
  }

  const onBomiStatusButtonClick = (data) => {
    setSAPDeliveryNo(data.sap_delivery_number)
    refBomiStatusDialog.current.show()
  }
  const popoverContentShortcuts = (data) => {
    return (
      <Menu>
        {userHasMenu(['order_confirmations']) &&
        <MenuItem
          icon='shopping-cart'
          text={t('title_order_shortcuts')}
          onClick={() => {
            setSAPDeliveryNo(data.sap_delivery_number)
            refOrderShortcutsDialog.current.show()
          }}
        />
        }
        {userHasMenu(['invoice']) &&
        <MenuItem
          icon='document'
          text={t('title_invoice_shortcuts')}
          onClick={() => {
            setSAPDeliveryNo(data.sap_delivery_number)
            refInvoiceShortcutsDialog.current.show()
          }}
        />
        }
        {userHasMenu(['contract_list','antibody_contract_list']) &&
        <MenuItem
          icon='manually-entered-data'
          text={t('title_contract_shortcuts')}
          onClick={() => {
            setSAPDeliveryNo(data.sap_delivery_number)
            refContractShortcutsDialog.current.show()
          }}
        />
        }
      </Menu>
    )
  }

  const menuCellShortcuts = ({ data }) => {
    return (
      <Tooltip popoverClassName='blueprintTooltip' content={t('shortcuts')} position={Position.BOTTOM}>
        <Popover
          position={Position.BOTTOM_LEFT}
          content={popoverContentShortcuts(data)}
          target={<Icon icon='share' className='bp3-grid-icon-shortcut' />}
          interactionKind={PopoverInteractionKind.CLICK}
        />
      </Tooltip>
    )
  }

  const rowDoubleClick = (e, data) => {
    const component = e.component
    const prevClickTime = component.lastClickTime
    component.lastClickTime = new Date()
    if (prevClickTime && (component.lastClickTime - prevClickTime < 300)) {
      onViewCellClick(e.data)
    }
  }

  return (
    <>
      <BPDialogV2
        ref={refDeliveryDetailDialog}
        size='90'
        removeFooter
        title={t('page_title_delivery_details')}
        onClose={() => refDeliveryDetailDialog.current.hide()}
      >
        <DeliveryDetail documentNo={documentNo} />
      </BPDialogV2>
      <BPDialogV2
        ref={refBomiStatusDialog}
        size='60'
        removeFooter
        title={t('page_title_delivery_bomi_status')}
        onClose={() => refBomiStatusDialog.current.hide()}
        id="bomi_status_dialog"
      >
        <DeliveryBomiStatus sapDeliveryNumber={SAPDeliveryNo} />
      </BPDialogV2>
      <BPDialogV2
        ref={refPrintDialog}
        size='90'
        showChange={false}
        title={t('page_print')}
        onClose={() => refPrintDialog.current.hide()}
      >
        <Stimulsoft
          data={printData}
          mrt={`${i18n.language === 'tr' ? 'DeliveryTR.mrt' : 'DeliveryEN.mrt'}`}
          customHeight='130'
        />
      </BPDialogV2>
      <BPDialogV2
        ref={refOrderShortcutsDialog}
        size='90'
        removeFooter
        title={t('page_title_order_shortcuts')}
        onClose={() => {
          refOrderShortcutsDialog.current.hide()
        }}
        canEscapeKeyClose
        canOutsideClickClose
      >
        <OrderShortcuts sap_delivery_number={SAPDeliveryNo} />
      </BPDialogV2>
      <BPDialogV2
        ref={refInvoiceShortcutsDialog}
        size='90'
        removeFooter
        title={t('page_title_invoice_shortcuts')}
        onClose={() => {
          refInvoiceShortcutsDialog.current.hide()
        }}
        canEscapeKeyClose
        canOutsideClickClose
      >
        <InvoiceShortcuts sap_delivery_number={SAPDeliveryNo} />
      </BPDialogV2>
      <BPDialogV2
        ref={refContractShortcutsDialog}
        size='90'
        removeFooter
        title={t('page_title_contract_shortcuts')}
        onClose={() => {
          refContractShortcutsDialog.current.hide()
        }}
        canEscapeKeyClose
        canOutsideClickClose
      >
        <ContractShortcuts sap_delivery_number={SAPDeliveryNo} />
      </BPDialogV2>
      <DataGrid
        ref={refGrid}
        dataSource={deliveries}
        onToolbarPreparing={onToolbarPreparing}
        allowColumnReordering
        showBorders
        selection={{ mode: 'single' }}
        hoverStateEnabled
        keyExpr='pi_message'
        showRowLines
        remoteOperations
        columnAutoWidth
        rowAlternationEnabled
        focusedRowEnabled
        width='100%'
        onContentReady={(e) => {
          e.component.option('height', document.getElementsByClassName('bp3-dialog')[0].clientHeight - 80)
        }}
        allowColumnResizing
        columnResizingMode='widget'
        onRowClick={rowDoubleClick}
      >
        <Export enabled={false} fileName={generateExportFileName} allowExportSelectedData={false} />
        <FilterRow visible />
        <HeaderFilter searchMode="contains" visible allowSearch />
        <SearchPanel width={240} placeholder={t('input_search')} visible searchVisibleColumnsOnly />
        <Paging enabled={false} defaultPageSize={20} />
        <Pager
          showPageSizeSelector
          allowedPageSizes={[20, 50, 100]}
          showNavigationButtons
          showInfo
          infoText={t('pager_info_text')}
          visible={false}
        />
        <Scrolling mode='standart' />
        <Column
          cellRender={({ data }) => <ViewCell data={data} onClick={onViewCellClick} />}
          width={36}
          allowSorting={false}
          alignment='center'
          allowReordering={false}
          allowExporting={false}
        />

        <Column
          cellRender={({ data }) =>
            data.bomi_last_status && (
              <GridButton
                label={data.bomi_last_status}
                data={data}
                tooltip={data.bomi_last_status}
                icon='info-sign'
                className='bp3-intent-list deliveryStatusButton'
                onClick={onBomiStatusButtonClick}
              />
          )}
          dataField='bomi_last_status'
          caption={t('bomi_last_status')}
          alignment='center'
          // width={150}
        />
        <Column
          cellRender={({ data }) => <PrintCell data={data} onClick={onPrintCellClick} />}
          width={36}
          allowSorting={false}
          alignment='center'
          allowReordering={false}
          allowExporting={false}
        />
        {userHasMenu(['contract_list', 'antibody_contract_list','invoice','order_confirmations']) &&
        <Column
          cellRender={menuCellShortcuts}
          width={36}
          allowSorting={false}
          alignment='center'
          allowReordering={false}
          allowExporting={false}
          name='colShortcuts'
        />
        }
        <Column dataField='sap_delivery_number' caption={t('document_no')} alignment='center' />
        <Column
          dataField='doc_date'
          caption={t('date')}
          dataType='date'
          format='dd.MM.yyyy'
          width={120}
          alignment='center'
        />
        <Column dataField='custdesc' caption={t('customer')} />
        <Column dataField='shipto' caption={t('shipping_address')} />
        <Column dataField='Description' caption={t('description')} />
        <Column dataField='legal_number' caption={t('legal_number')} />
        <Template name='refreshButton' render={() => <ToolbarRefreshButton onClick={getDeliveries} />} />
        <Template
          name='exportButton'
          render={() => (
            <ToolbarExportButton
              onClick={() => {
                refGrid.current._instance.exportToExcel(false)
              }}
            />
          )}
        />
      </DataGrid>
    </>
  )
}

const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift(
    {
      location: 'after',
      template: 'refreshButton'
    },
    {
      location: 'after',
      template: 'exportButton'
    }
  )
}

export default DeliveryShortcuts
